import { createContext, useContext, useReducer, useCallback, useEffect, ReactNode } from 'react';

// Debug event types
export type EventSource = 'sdk' | 'console' | 'edge' | 'mobile' | 'lattice' | 'circuit' | 'witness';
export type EventCategory = 'auth' | 'navigation' | 'api' | 'error' | 'info' | 'lifecycle' | 'execution' | 'attestation' | 'circuit-action' | 'timing';
export type EventSeverity = 'debug' | 'info' | 'warning' | 'error';

export interface DebugEvent {
  id: string;
  timestamp: number;
  source: EventSource;
  category: EventCategory;
  correlationId?: string;
  message: string;
  data?: Record<string, unknown>;
  // Lattice-specific fields
  latticeId?: string;
  circuitId?: string;
  nodeId?: string;
  severity?: EventSeverity;
  // Timing fields
  duration?: number;
  // API fields
  endpoint?: string;
  method?: string;
  status?: number;
}

// Error aggregation with stack hash
export interface ErrorAggregate {
  stackHash: string;
  message: string;
  count: number;
  firstSeen: number;
  lastSeen: number;
  sampleStack?: string;
  sampleData?: Record<string, unknown>;
}

// Performance timing stats
export interface TimingStats {
  operation: string;
  count: number;
  totalMs: number;
  avgMs: number;
  minMs: number;
  maxMs: number;
  p95Ms: number;
  samples: number[];
}

// Debug state
interface DebugState {
  events: DebugEvent[];
  isVisible: boolean;
  filter: {
    sources: Set<EventSource>;
    categories: Set<EventCategory>;
  };
  maxEvents: number;
  logLevel: EventSeverity;
  // Error aggregation
  errorAggregates: Map<string, ErrorAggregate>;
  // Performance timing
  timingStats: Map<string, TimingStats>;
  // Remote config
  remoteConfig: {
    loaded: boolean;
    logLevel?: EventSeverity;
    enabledCategories?: EventCategory[];
  };
}

// Actions
type DebugAction =
  | { type: 'ADD_EVENT'; event: DebugEvent }
  | { type: 'CLEAR_EVENTS' }
  | { type: 'TOGGLE_VISIBLE' }
  | { type: 'SET_VISIBLE'; visible: boolean }
  | { type: 'TOGGLE_SOURCE'; source: EventSource }
  | { type: 'TOGGLE_CATEGORY'; category: EventCategory }
  | { type: 'SET_LOG_LEVEL'; level: EventSeverity }
  | { type: 'SET_CATEGORIES'; categories: EventCategory[] }
  | { type: 'LOAD_REMOTE_CONFIG'; config: { logLevel?: EventSeverity; enabledCategories?: EventCategory[] } };

// Generate a hash from error stack trace for grouping
function generateStackHash(stack?: string): string {
  if (!stack) return 'no-stack';
  
  // Extract the first 3 stack frames for grouping
  const frames = stack.split('\n').slice(0, 4).join('\n');
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < frames.length; i++) {
    const char = frames.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

// Update timing stats
function updateTimingStats(
  stats: Map<string, TimingStats>,
  operation: string,
  durationMs: number
): Map<string, TimingStats> {
  const newStats = new Map(stats);
  const existing = newStats.get(operation);
  
  if (existing) {
    const samples = [...existing.samples, durationMs].slice(-100); // Keep last 100 samples
    const sorted = [...samples].sort((a, b) => a - b);
    const p95Index = Math.floor(sorted.length * 0.95);
    
    newStats.set(operation, {
      operation,
      count: existing.count + 1,
      totalMs: existing.totalMs + durationMs,
      avgMs: Math.round((existing.totalMs + durationMs) / (existing.count + 1)),
      minMs: Math.min(existing.minMs, durationMs),
      maxMs: Math.max(existing.maxMs, durationMs),
      p95Ms: sorted[p95Index] || durationMs,
      samples,
    });
  } else {
    newStats.set(operation, {
      operation,
      count: 1,
      totalMs: durationMs,
      avgMs: durationMs,
      minMs: durationMs,
      maxMs: durationMs,
      p95Ms: durationMs,
      samples: [durationMs],
    });
  }
  
  return newStats;
}

// Log level settings storage key
const LOG_SETTINGS_KEY = 'estream-debug-settings';

// Load saved settings
function loadLogSettings(): { logLevel: EventSeverity; enabledCategories: EventCategory[] } {
  try {
    const stored = localStorage.getItem(LOG_SETTINGS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore
  }
  return {
    logLevel: 'info',
    enabledCategories: ['auth', 'navigation', 'api', 'error', 'info', 'lifecycle', 'execution', 'attestation', 'circuit-action', 'timing'],
  };
}

// Save settings
export function saveLogSettings(settings: { logLevel: EventSeverity; enabledCategories: EventCategory[] }) {
  try {
    localStorage.setItem(LOG_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Ignore
  }
}

// All available categories
export const ALL_CATEGORIES: EventCategory[] = [
  'auth', 'navigation', 'api', 'error', 'info', 
  'lifecycle', 'execution', 'attestation', 'circuit-action', 'timing'
];

// All available sources
export const ALL_SOURCES: EventSource[] = [
  'sdk', 'console', 'edge', 'mobile', 'lattice', 'circuit', 'witness'
];

// Initial state
const savedSettings = loadLogSettings();
const initialState: DebugState = {
  events: [],
  isVisible: false,
  filter: {
    sources: new Set(ALL_SOURCES),
    categories: new Set(savedSettings.enabledCategories),
  },
  maxEvents: 500,
  logLevel: savedSettings.logLevel,
  errorAggregates: new Map(),
  timingStats: new Map(),
  remoteConfig: { loaded: false },
};

// Reducer
function debugReducer(state: DebugState, action: DebugAction): DebugState {
  switch (action.type) {
    case 'ADD_EVENT': {
      const events = [action.event, ...state.events].slice(0, state.maxEvents);
      let errorAggregates = state.errorAggregates;
      let timingStats = state.timingStats;
      
      // Aggregate errors by stack hash
      if (action.event.category === 'error' && action.event.data?.stack) {
        const stackHash = generateStackHash(action.event.data.stack as string);
        const existing = state.errorAggregates.get(stackHash);
        
        errorAggregates = new Map(state.errorAggregates);
        if (existing) {
          errorAggregates.set(stackHash, {
            ...existing,
            count: existing.count + 1,
            lastSeen: action.event.timestamp,
          });
        } else {
          errorAggregates.set(stackHash, {
            stackHash,
            message: action.event.message,
            count: 1,
            firstSeen: action.event.timestamp,
            lastSeen: action.event.timestamp,
            sampleStack: action.event.data.stack as string,
            sampleData: action.event.data,
          });
        }
      }
      
      // Track timing stats
      if (action.event.category === 'timing' && action.event.duration) {
        const operation = action.event.endpoint || action.event.message.split(':')[0] || 'unknown';
        timingStats = updateTimingStats(state.timingStats, operation, action.event.duration);
      }
      
      // Track API timing
      if (action.event.category === 'api' && action.event.duration && action.event.endpoint) {
        timingStats = updateTimingStats(state.timingStats, action.event.endpoint, action.event.duration);
      }
      
      return { ...state, events, errorAggregates, timingStats };
    }
    case 'CLEAR_EVENTS':
      return { ...state, events: [], errorAggregates: new Map(), timingStats: new Map() };
    case 'TOGGLE_VISIBLE':
      return { ...state, isVisible: !state.isVisible };
    case 'SET_VISIBLE':
      return { ...state, isVisible: action.visible };
    case 'TOGGLE_SOURCE': {
      const sources = new Set(state.filter.sources);
      if (sources.has(action.source)) {
        sources.delete(action.source);
      } else {
        sources.add(action.source);
      }
      return { ...state, filter: { ...state.filter, sources } };
    }
    case 'TOGGLE_CATEGORY': {
      const categories = new Set(state.filter.categories);
      if (categories.has(action.category)) {
        categories.delete(action.category);
      } else {
        categories.add(action.category);
      }
      return { ...state, filter: { ...state.filter, categories } };
    }
    case 'SET_LOG_LEVEL':
      return { ...state, logLevel: action.level };
    case 'SET_CATEGORIES':
      return { ...state, filter: { ...state.filter, categories: new Set(action.categories) } };
    case 'LOAD_REMOTE_CONFIG': {
      const newState = {
        ...state,
        remoteConfig: { loaded: true, ...action.config },
      };
      if (action.config.logLevel) {
        newState.logLevel = action.config.logLevel;
      }
      if (action.config.enabledCategories) {
        newState.filter = {
          ...newState.filter,
          categories: new Set(action.config.enabledCategories),
        };
      }
      return newState;
    }
    default:
      return state;
  }
}

// Context
interface DebugContextValue {
  state: DebugState;
  addEvent: (event: Omit<DebugEvent, 'id' | 'timestamp'>) => void;
  clearEvents: () => void;
  toggleVisible: () => void;
  setVisible: (visible: boolean) => void;
  toggleSource: (source: EventSource) => void;
  toggleCategory: (category: EventCategory) => void;
  setLogLevel: (level: EventSeverity) => void;
  setCategories: (categories: EventCategory[]) => void;
  exportEvents: () => string;
  filteredEvents: DebugEvent[];
  // New: error aggregates
  errorAggregates: ErrorAggregate[];
  // New: timing stats
  timingStats: TimingStats[];
  // New: load remote config
  loadRemoteConfig: () => Promise<void>;
}

const DebugContext = createContext<DebugContextValue | null>(null);

// Provider
export function DebugProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(debugReducer, initialState);

  const addEvent = useCallback(
    (event: Omit<DebugEvent, 'id' | 'timestamp'>) => {
      const fullEvent: DebugEvent = {
        ...event,
        id: crypto.randomUUID(),
        timestamp: Date.now(),
      };
      dispatch({ type: 'ADD_EVENT', event: fullEvent });
      
      // Also log to console in dev
      if (import.meta.env.DEV) {
        const style = event.category === 'error' 
          ? 'color: #ff4444' 
          : event.category === 'auth' 
          ? 'color: #44ff44'
          : 'color: #4488ff';
        console.log(`%c[${event.source}/${event.category}] ${event.message}`, style, event.data);
      }
    },
    []
  );

  const clearEvents = useCallback(() => dispatch({ type: 'CLEAR_EVENTS' }), []);
  const toggleVisible = useCallback(() => dispatch({ type: 'TOGGLE_VISIBLE' }), []);
  const setVisible = useCallback((visible: boolean) => dispatch({ type: 'SET_VISIBLE', visible }), []);
  const toggleSource = useCallback((source: EventSource) => 
    dispatch({ type: 'TOGGLE_SOURCE', source }), []);
  const toggleCategory = useCallback((category: EventCategory) => 
    dispatch({ type: 'TOGGLE_CATEGORY', category }), []);
  const setLogLevel = useCallback((level: EventSeverity) => {
    dispatch({ type: 'SET_LOG_LEVEL', level });
    const savedSettings = loadLogSettings();
    saveLogSettings({ ...savedSettings, logLevel: level });
  }, []);
  const setCategories = useCallback((categories: EventCategory[]) => {
    dispatch({ type: 'SET_CATEGORIES', categories });
    const savedSettings = loadLogSettings();
    saveLogSettings({ ...savedSettings, enabledCategories: categories });
  }, []);
  
  // Load remote config from edge proxy
  const loadRemoteConfig = useCallback(async () => {
    try {
      const edgeUrl = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';
      const response = await fetch(`${edgeUrl}/api/config/debug`, {
        headers: { 'Accept': 'application/json' },
      });
      
      if (response.ok) {
        const config = await response.json();
        dispatch({
          type: 'LOAD_REMOTE_CONFIG',
          config: {
            logLevel: config.log_level as EventSeverity,
            enabledCategories: config.enabled_categories as EventCategory[],
          },
        });
        
        addEvent({
          source: 'console',
          category: 'info',
          message: 'Loaded remote debug config',
          data: config,
        });
      }
    } catch (error) {
      // Silently fail - use local settings
      console.debug('[DebugContext] Remote config not available');
    }
  }, [addEvent]);

  // Load remote config on mount
  useEffect(() => {
    loadRemoteConfig();
  }, [loadRemoteConfig]);
  
  // Export events in debug_trace format for estream-test compatibility
  const exportEvents = useCallback(() => {
    const journey = {
      journey_id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      events: state.events.map(e => ({
        timestamp: new Date(e.timestamp).toISOString(),
        source: e.source,
        category: e.category,
        message: e.message,
        severity: e.severity || 'info',
        correlation_id: e.correlationId,
        data: e.data,
        lattice_id: e.latticeId,
        circuit_id: e.circuitId,
        node_id: e.nodeId,
        duration_ms: e.duration,
        endpoint: e.endpoint,
        method: e.method,
        status: e.status,
      })),
    };
    return JSON.stringify(journey, null, 2);
  }, [state.events]);

  // Keyboard shortcut: Ctrl+Shift+D
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        toggleVisible();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleVisible]);

  // Filter events
  const filteredEvents = state.events.filter(
    (event) =>
      state.filter.sources.has(event.source) &&
      state.filter.categories.has(event.category)
  );

  // Convert maps to arrays for easy consumption
  const errorAggregates = Array.from(state.errorAggregates.values())
    .sort((a, b) => b.count - a.count);
  
  const timingStats = Array.from(state.timingStats.values())
    .sort((a, b) => b.avgMs - a.avgMs);

  return (
    <DebugContext.Provider
      value={{
        state,
        addEvent,
        clearEvents,
        toggleVisible,
        setVisible,
        toggleSource,
        toggleCategory,
        setLogLevel,
        setCategories,
        exportEvents,
        filteredEvents,
        errorAggregates,
        timingStats,
        loadRemoteConfig,
      }}
    >
      {children}
    </DebugContext.Provider>
  );
}

// Hook
export function useDebug() {
  const context = useContext(DebugContext);
  if (!context) {
    throw new Error('useDebug must be used within DebugProvider');
  }
  return context;
}

// Global event emitter for SDK instrumentation
let globalDebugEmitter: ((event: Omit<DebugEvent, 'id' | 'timestamp'>) => void) | null = null;

export function setGlobalDebugEmitter(emitter: typeof globalDebugEmitter) {
  globalDebugEmitter = emitter;
}

export function emitDebugEvent(event: Omit<DebugEvent, 'id' | 'timestamp'>) {
  if (globalDebugEmitter) {
    globalDebugEmitter(event);
  }
}
